import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;
const MENU_OPTIONS = ['FM Courses', 'Management Courses', 'Key Courses'];
// Sub-menus nested under each top-level menu in the site header. Most FM
// Courses still sit directly under the top-level dropdown — International FM
// Diploma Programme is the one sub-menu grouping under it so far.
const SUB_CATEGORY_OPTIONS = {
  'FM Courses': [
    'International FM Diploma Programme',
  ],
  'Management Courses': [
    'Artificial Intelligence Course',
    'Human Resources Management Courses',
    'Finance and Accounting',
    'Purchase and Procurements',
    'Project Management',
    'Logistics and Supply Chain',
    'Business Management',
    'Quality & Process Management',
  ],
  'Key Courses': [
    'Soft Skills',
    'Language Training Courses',
  ],
};

function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function AdminCoursePageEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [intro, setIntro] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [menuCategory, setMenuCategory] = useState(MENU_OPTIONS[0]);
  const [subCategory, setSubCategory] = useState('');
  const [titleFontSize, setTitleFontSize] = useState(36);
  const [titleBold, setTitleBold] = useState(true);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [existingCover, setExistingCover] = useState('');
  const [secondaryImage, setSecondaryImage] = useState(null);
  const [secondaryPreview, setSecondaryPreview] = useState(null);
  const [existingSecondary, setExistingSecondary] = useState('');
  const [body, setBody] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [status, setStatus] = useState(1);

  const [gallery, setGallery] = useState([]);
  const [galleryType, setGalleryType] = useState('image');
  const [galleryImage, setGalleryImage] = useState(null);
  const [galleryVideoUrl, setGalleryVideoUrl] = useState('');
  const [galleryCaption, setGalleryCaption] = useState('');
  const [galleryError, setGalleryError] = useState('');
  const [galleryBusy, setGalleryBusy] = useState(false);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchCoursePage = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/course-pages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const c = response.data.coursePage;
        setTitle(c.title);
        setSubtitle(c.subtitle || '');
        setIntro(c.intro || '');
        setSlug(c.slug);
        setSlugTouched(true);
        setMenuCategory(c.menu_category);
        setSubCategory(c.sub_category || '');
        setTitleFontSize(c.title_font_size || 36);
        setTitleBold(!!c.title_bold);
        setExistingCover(c.cover_image || '');
        setExistingSecondary(c.secondary_image || '');
        setBody(c.body || '');
        setMetaTitle(c.meta_title || '');
        setMetaDescription(c.meta_description || '');
        setMetaKeywords(c.meta_keywords || '');
        setStatus(c.status);
        setGallery(response.data.gallery || []);
      } else {
        setError(response.data.message || 'Failed to fetch course page.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching course page.');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    fetchCoursePage();
  }, [fetchCoursePage]);

  const handleTitleChange = (value) => {
    setTitle(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setCoverPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSecondaryChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSecondaryImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setSecondaryPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    setSaving(true);

    const formData = new FormData();
    formData.append('title', title);
    formData.append('subtitle', subtitle);
    formData.append('intro', intro);
    formData.append('slug', slug);
    formData.append('menu_category', menuCategory);
    formData.append('sub_category', SUB_CATEGORY_OPTIONS[menuCategory] ? subCategory : '');
    formData.append('title_font_size', titleFontSize);
    formData.append('title_bold', titleBold);
    formData.append('body', body);
    formData.append('meta_title', metaTitle);
    formData.append('meta_description', metaDescription);
    formData.append('meta_keywords', metaKeywords);
    formData.append('status', status);
    if (coverImage) formData.append('cover_image', coverImage);
    if (secondaryImage) formData.append('secondary_image', secondaryImage);

    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setSaving(false);
        return;
      }
      const url = isEdit ? `${apiUrl}/api/admin/course-pages/${id}` : `${apiUrl}/api/admin/course-pages`;
      const method = isEdit ? 'put' : 'post';
      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        if (isEdit) {
          setSuccess('Course page updated successfully.');
          fetchCoursePage();
        } else {
          navigate(`/admin-course-pages/edit/${response.data.id}`);
        }
      } else {
        setError(response.data.message || 'Failed to save course page.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving course page.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGalleryItem = async (e) => {
    e.preventDefault();
    setGalleryError('');
    setGalleryBusy(true);
    const formData = new FormData();
    formData.append('type', galleryType);
    formData.append('caption', galleryCaption);
    if (galleryType === 'video') {
      formData.append('video_url', galleryVideoUrl);
    } else if (galleryImage) {
      formData.append('image', galleryImage);
    }
    try {
      const token = getToken();
      const response = await axios.post(`${apiUrl}/api/admin/course-pages/${id}/gallery`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setGalleryImage(null);
        setGalleryVideoUrl('');
        setGalleryCaption('');
        fetchCoursePage();
      } else {
        setGalleryError(response.data.message || 'Failed to add gallery item.');
      }
    } catch (err) {
      setGalleryError(err.response?.data?.message || 'Error adding gallery item.');
    } finally {
      setGalleryBusy(false);
    }
  };

  const handleDeleteGalleryItem = async (galleryId) => {
    if (!window.confirm('Remove this gallery item?')) return;
    try {
      const token = getToken();
      await axios.delete(`${apiUrl}/api/admin/course-gallery/${galleryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCoursePage();
    } catch (err) {
      setGalleryError(err.response?.data?.message || 'Error removing gallery item.');
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li>
                  <Link to="/admin-course-pages" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Course Pages
                  </Link>
                </li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">{isEdit ? 'Edit' : 'Add New'}</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {loading ? (
            <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Page Details</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-8">
                      <label htmlFor="title" className="h5 mb-8 fw-semibold font-heading">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        className="form-control py-11"
                        value={title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        disabled={saving}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="menuCategory" className="h5 mb-8 fw-semibold font-heading">
                        Menu <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="menuCategory"
                        className="form-select py-11"
                        value={menuCategory}
                        onChange={(e) => {
                          setMenuCategory(e.target.value);
                          setSubCategory('');
                        }}
                        disabled={saving}
                      >
                        {MENU_OPTIONS.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    {SUB_CATEGORY_OPTIONS[menuCategory] && (
                      <div className="col-md-4">
                        <label htmlFor="subCategory" className="h5 mb-8 fw-semibold font-heading">
                          Sub-menu <span className="text-gray-400 fw-normal text-13">(optional)</span>
                        </label>
                        <select
                          id="subCategory"
                          className="form-select py-11"
                          value={subCategory}
                          onChange={(e) => setSubCategory(e.target.value)}
                          disabled={saving}
                        >
                          <option value="">None — show directly under {menuCategory}</option>
                          {SUB_CATEGORY_OPTIONS[menuCategory].map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="col-12">
                      <label htmlFor="subtitle" className="h5 mb-8 fw-semibold font-heading">
                        Subtitle <span className="text-gray-400 fw-normal text-13">(optional tagline shown under the title)</span>
                      </label>
                      <input
                        type="text"
                        id="subtitle"
                        className="form-control py-11"
                        value={subtitle}
                        onChange={(e) => setSubtitle(e.target.value)}
                        disabled={saving}
                      />
                    </div>

                    <div className="col-md-8">
                      <label htmlFor="slug" className="h5 mb-8 fw-semibold font-heading">
                        Path <span className="text-red-500">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">ifmedu.in/</span>
                        <input
                          type="text"
                          id="slug"
                          className="form-control py-11"
                          value={slug}
                          onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
                          disabled={saving}
                          required
                        />
                      </div>
                      <span className="text-13 text-gray-400">Lowercase letters, numbers and hyphens only. Auto-filled from the title, but you can override it.</span>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="status" className="h5 mb-8 fw-semibold font-heading">
                        Status
                      </label>
                      <select
                        id="status"
                        className="form-select py-11"
                        value={status}
                        onChange={(e) => setStatus(Number(e.target.value))}
                        disabled={saving}
                      >
                        <option value={1}>Published</option>
                        <option value={0}>Draft</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">
                        Title Style <span className="text-gray-400 fw-normal text-13">(how the heading appears on the page)</span>
                      </label>
                      <div className="d-flex align-items-center flex-wrap gap-16">
                        <div className="d-flex align-items-center gap-8">
                          <label htmlFor="titleFontSize" className="text-14 text-gray-500 mb-0">Size (px)</label>
                          <input
                            type="number"
                            id="titleFontSize"
                            className="form-control py-8"
                            style={{ width: 90 }}
                            min={16}
                            max={80}
                            value={titleFontSize}
                            onChange={(e) => setTitleFontSize(e.target.value)}
                            disabled={saving}
                          />
                        </div>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="titleBold"
                            checked={titleBold}
                            onChange={(e) => setTitleBold(e.target.checked)}
                            disabled={saving}
                          />
                          <label className="form-check-label text-14" htmlFor="titleBold">Bold</label>
                        </div>
                        <span
                          className="text-gray-800"
                          style={{ fontSize: `${Math.min(titleFontSize, 36)}px`, fontWeight: titleBold ? 700 : 400 }}
                        >
                          {title || 'Preview title'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Images</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">Cover Image</label>
                      <div className="d-flex align-items-center gap-16">
                        {(coverPreview || existingCover) ? (
                          <img
                            src={coverPreview || `${apiUrl}/${existingCover}`}
                            alt="cover preview"
                            style={{ width: 120, height: 90, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                          />
                        ) : (
                          <div style={{
                            width: 120, height: 90, borderRadius: 12, background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db'
                          }}>
                            <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={handleCoverChange}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">Secondary Image</label>
                      <div className="d-flex align-items-center gap-16">
                        {(secondaryPreview || existingSecondary) ? (
                          <img
                            src={secondaryPreview || `${apiUrl}/${existingSecondary}`}
                            alt="secondary preview"
                            style={{ width: 120, height: 90, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                          />
                        ) : (
                          <div style={{
                            width: 120, height: 90, borderRadius: 12, background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db'
                          }}>
                            <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={handleSecondaryChange}
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Intro</h5>
                  <span className="text-13 text-gray-400 d-block mt-4">
                    Short intro paragraph(s) shown next to the cover image, above the Call button.
                  </span>
                </div>
                <div className="card-body">
                  <CKEditor
                    editor={ClassicEditor}
                    data={intro}
                    onChange={(event, editor) => setIntro(editor.getData())}
                    config={{
                      toolbar: ['bold', 'italic', 'link', '|', 'undo', 'redo']
                    }}
                  />
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Body Content</h5>
                </div>
                <div className="card-body">
                  <CKEditor
                    editor={ClassicEditor}
                    data={body}
                    onChange={(event, editor) => setBody(editor.getData())}
                    config={{
                      toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                        'blockQuote', 'insertTable', '|',
                        'undo', 'redo'
                      ]
                    }}
                  />
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">SEO / Meta Tags</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-12">
                      <label htmlFor="metaTitle" className="h5 mb-8 fw-semibold font-heading">Meta Title</label>
                      <input
                        type="text"
                        id="metaTitle"
                        className="form-control py-11"
                        value={metaTitle}
                        onChange={(e) => setMetaTitle(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="metaDescription" className="h5 mb-8 fw-semibold font-heading">Meta Description</label>
                      <textarea
                        id="metaDescription"
                        className="form-control py-11"
                        rows={3}
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="metaKeywords" className="h5 mb-8 fw-semibold font-heading">Meta Keywords</label>
                      <input
                        type="text"
                        id="metaKeywords"
                        className="form-control py-11"
                        placeholder="comma, separated, keywords"
                        value={metaKeywords}
                        onChange={(e) => setMetaKeywords(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-8 mb-24">
                <Link to="/admin-course-pages" className="btn btn-outline-main rounded-pill py-9">Cancel</Link>
                <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                  {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Course Page'}
                </button>
              </div>
            </form>
          )}

          {isEdit && !loading && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">Gallery (Images / Videos)</h5>
                <span className="text-13 text-gray-400 d-block mt-4">
                  Shown at the bottom of the course page. Hidden automatically if empty.
                </span>
              </div>
              <div className="card-body">
                {galleryError && <div className="alert alert-danger mb-16">{galleryError}</div>}
                <form onSubmit={handleAddGalleryItem} className="mb-24">
                  <div className="row g-16 align-items-end">
                    <div className="col-md-2">
                      <label className="text-14 text-gray-500 mb-8 d-block">Type</label>
                      <select
                        className="form-select py-11"
                        value={galleryType}
                        onChange={(e) => setGalleryType(e.target.value)}
                        disabled={galleryBusy}
                      >
                        <option value="image">Image</option>
                        <option value="video">Video (link)</option>
                      </select>
                    </div>
                    {galleryType === 'image' ? (
                      <div className="col-md-4">
                        <label className="text-14 text-gray-500 mb-8 d-block">Image</label>
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={(e) => setGalleryImage(e.target.files[0])}
                          disabled={galleryBusy}
                          required
                        />
                      </div>
                    ) : (
                      <div className="col-md-4">
                        <label className="text-14 text-gray-500 mb-8 d-block">Video URL</label>
                        <input
                          type="url"
                          className="form-control py-11"
                          placeholder="https://youtube.com/..."
                          value={galleryVideoUrl}
                          onChange={(e) => setGalleryVideoUrl(e.target.value)}
                          disabled={galleryBusy}
                          required
                        />
                      </div>
                    )}
                    <div className="col-md-4">
                      <label className="text-14 text-gray-500 mb-8 d-block">Caption (optional)</label>
                      <input
                        type="text"
                        className="form-control py-11"
                        value={galleryCaption}
                        onChange={(e) => setGalleryCaption(e.target.value)}
                        disabled={galleryBusy}
                      />
                    </div>
                    <div className="col-md-2">
                      <button type="submit" className="btn btn-main rounded-pill py-11 w-100" disabled={galleryBusy}>
                        {galleryBusy ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </div>
                </form>

                {gallery.length === 0 ? (
                  <div className="text-gray-400 text-14">No gallery items yet — the gallery section will be hidden on the live page.</div>
                ) : (
                  <div className="row g-16">
                    {gallery.map((item) => (
                      <div className="col-sm-6 col-lg-3" key={item.id}>
                        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: 12, overflow: 'hidden' }}>
                          {item.type === 'image' ? (
                            <img
                              src={`${apiUrl}/${item.media_path}`}
                              alt={item.caption || 'gallery'}
                              style={{ width: '100%', height: 120, objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: 120, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <i className="ph ph-play-circle text-white" style={{ fontSize: 32 }} />
                            </div>
                          )}
                          <div className="card-body py-10 px-12">
                            <span className="text-13 text-gray-500 d-block text-truncate mb-8">
                              {item.caption || (item.type === 'video' ? item.video_url : 'Image')}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm rounded-pill py-4 px-12 w-100"
                              style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff' }}
                              onClick={() => handleDeleteGalleryItem(item.id)}
                            >
                              <i className="ph ph-trash me-1" /> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {!isEdit && (
            <div className="alert alert-info">
              Save the course page first — the gallery (images/videos) can be added once it's created.
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminCoursePageEditor;
